/**
 * Keeps the cookie banner off the primary call to action.
 *
 * Shopify renders its consent banner as `#shopify-pc__banner`, fixed to the
 * bottom of the viewport with `z-index: 2000000`. Measured on the live cart
 * page at 390x664 (iPhone 13): the banner is 147px tall and covers 517-664px,
 * while the "Check out" button sits at 573-625px. On arrival the shopper's
 * only way forward is completely hidden behind the banner, and a tap where the
 * button looks like it should be lands on the banner instead.
 *
 * Nothing can be moved out of the way in CSS alone, because the banner is an
 * overlay and its height depends on the text, the language and the viewport.
 * So this measures the banner while it is up and publishes two things:
 *
 *   --consent-inset          the banner's live height, 0px when it is gone
 *   html.consent-banner-up   present only while the banner is on screen
 *
 * `assets/hybrid-conversion.css` uses both to float the cart's checkout button
 * clear of the banner on phones. The moment the shopper accepts or declines,
 * the banner leaves the DOM, the flag drops, the inset returns to 0px and every
 * page is laid out exactly as it was before.
 */
(function () {
  const SELECTOR = '#shopify-pc__banner, .shopify-pc__banner';
  const FLAG = 'consent-banner-up';
  const root = document.documentElement;

  let frame = null;
  let watched = null;
  let sizeObserver = null;

  function apply(height) {
    const inset = height > 0 ? `${Math.ceil(height)}px` : '0px';
    if (root.style.getPropertyValue('--consent-inset') !== inset) {
      root.style.setProperty('--consent-inset', inset);
    }
    root.classList.toggle(FLAG, height > 0);
  }

  function measure() {
    frame = null;
    const banner = document.querySelector(SELECTOR);

    if (!banner) {
      if (sizeObserver) sizeObserver.disconnect();
      watched = null;
      apply(0);
      return;
    }

    // A banner that is present but hidden (already answered, or still animating
    // in) must not reserve space, so trust the painted box rather than the DOM.
    const style = window.getComputedStyle(banner);
    const hidden = style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0';
    const height = hidden ? 0 : banner.getBoundingClientRect().height;

    apply(height);

    if (sizeObserver && watched !== banner) {
      sizeObserver.disconnect();
      sizeObserver.observe(banner);
      watched = banner;
    }
  }

  function schedule() {
    if (frame !== null) return;
    frame = window.requestAnimationFrame(measure);
  }

  if ('ResizeObserver' in window) {
    sizeObserver = new ResizeObserver(schedule);
  }

  // The banner is injected after the theme's own scripts and can be swapped or
  // removed at any point, so watch the whole body rather than one node.
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('orientationchange', schedule, { passive: true });
  document.addEventListener('visibilitychange', schedule);

  schedule();
})();
